import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

export default function Calendar({ initialEvents = {} }) {
  // Get authentication context
  const { user } = useAuth();
  
  // State for tracking current date and events
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState(initialEvents);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingEvent, setSavingEvent] = useState(null);
  
  // Format the current month and year for display
  const currentMonthDisplay = new Intl.DateTimeFormat('en-US', { 
    month: 'long', 
    year: 'numeric' 
  }).format(currentDate);
  
  // Get days in the current month
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  
  // Get the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
  
  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };
  
  const goToNextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };
  
  // Generate calendar dates
  const generateCalendarDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    
    // Get days from previous month to fill the first row
    const daysFromPrevMonth = firstDayOfMonth;
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevMonthYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevMonthYear, prevMonth);
    
    // Calculate days needed from next month
    const totalDaysToShow = 42; // 6 rows of 7 days
    const daysFromNextMonth = totalDaysToShow - daysInMonth - daysFromPrevMonth;
    
    // Create the calendar grid data
    let calendarDates = [];
    
    // Add days from previous month
    for (let i = daysInPrevMonth - daysFromPrevMonth + 1; i <= daysInPrevMonth; i++) {
      calendarDates.push({
        date: i,
        month: 'prev',
        fullDate: new Date(prevMonthYear, prevMonth, i)
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      calendarDates.push({
        date: i,
        month: 'current',
        fullDate: new Date(year, month, i)
      });
    }
    
    // Add days from next month
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextMonthYear = month === 11 ? year + 1 : year;
    
    for (let i = 1; i <= daysFromNextMonth; i++) {
      calendarDates.push({
        date: i,
        month: 'next',
        fullDate: new Date(nextMonthYear, nextMonth, i)
      });
    }
    
    // Organize into columns (by day of week)
    const columns = [[], [], [], [], [], [], []]; // One array per day of week
    calendarDates.forEach((dateObj, index) => {
      const dayOfWeek = index % 7;
      columns[dayOfWeek].push(dateObj);
    });
    
    return columns;
  };
  
  // Format date to use as key for events object
  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  };
  
  // Check if a date has an event
  const getEventType = (dateObj) => {
    const dateKey = formatDateKey(dateObj.fullDate);
    return events[dateKey] || '';
  };
  
  // Generate user-specific storage key
  const getStorageKey = () => {
    if (!user?.id) return 'calendar_events_guest';
    return `calendar_events_user_${user.id}`;
  };

  // Check if localStorage is available
  const isLocalStorageAvailable = () => {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Load calendar events from localStorage
  const loadCalendarEvents = async () => {
    if (!user) {
      return;
    }

    if (!isLocalStorageAvailable()) {
      console.warn('localStorage not available, calendar events will not persist');
      setError('Storage not available - events will not persist');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Simulate API delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const storageKey = getStorageKey();
      const storedEvents = localStorage.getItem(storageKey);
      
      if (storedEvents) {
        const parsedEvents = JSON.parse(storedEvents);
        setEvents(parsedEvents);
      } else {
        setEvents({});
      }
    } catch (error) {
      console.error('Error loading calendar events:', error);
      setError('Failed to load calendar events');
      setEvents({});
    } finally {
      setLoading(false);
    }
  };

  // Save calendar events to localStorage
  const saveEventToStorage = async (date, eventType) => {
    if (!user) {
      return;
    }

    if (!isLocalStorageAvailable()) {
      console.warn('localStorage not available, cannot save calendar event');
      setError('Storage not available - event not saved');
      return;
    }

    const dateKey = formatDateKey(date);
    
    try {
      setSavingEvent(dateKey);
      
      // Simulate API delay for better UX
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const storageKey = getStorageKey();
      
      // Get current events from localStorage
      const storedEvents = localStorage.getItem(storageKey);
      const currentEvents = storedEvents ? JSON.parse(storedEvents) : {};
      
      // Update events
      if (eventType === '') {
        // Delete event
        delete currentEvents[dateKey];
      } else {
        // Save/update event
        currentEvents[dateKey] = eventType;
      }
      
      // Save back to localStorage
      localStorage.setItem(storageKey, JSON.stringify(currentEvents));
      
    } catch (error) {
      console.error('Error saving calendar event:', error);
      setError('Failed to save calendar event');
      // Revert the change in UI
      setEvents(prev => {
        const updated = { ...prev };
        if (eventType === '') {
          // If we were trying to delete, restore the previous value
          const currentEvent = getEventType({ fullDate: date });
          if (currentEvent) {
            updated[dateKey] = currentEvent;
          }
        } else {
          // If we were trying to save, remove the change
          delete updated[dateKey];
        }
        return updated;
      });
    } finally {
      setSavingEvent(null);
    }
  };

  // Add a new event (with localStorage persistence)
  const addEvent = async (date, eventType) => {
    const dateKey = formatDateKey(date);
    
    // Update UI immediately for better UX
    setEvents(prev => {
      const updated = { ...prev };
      if (eventType === '') {
        delete updated[dateKey];
      } else {
        updated[dateKey] = eventType;
      }
      return updated;
    });

    // Save to localStorage in background
    await saveEventToStorage(date, eventType);
  };
  
  // Handle clicking on a date cell
  const handleDateClick = async (dateObj) => {
    // For demonstration, cycle through event types when clicking
    const eventTypes = ['planting', 'harvest', 'meeting', 'inspection', 'delivery', ''];
    const currentEventType = getEventType(dateObj);
    const currentIndex = eventTypes.indexOf(currentEventType);
    const nextIndex = (currentIndex + 1) % eventTypes.length;
    
    await addEvent(dateObj.fullDate, eventTypes[nextIndex]);
  };
  
  // Load calendar events when component mounts or user changes
  useEffect(() => {
    loadCalendarEvents();
  }, [user]); // Re-load when user changes

  // Clear error after a few seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  
  const calendarColumns = generateCalendarDates();
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  return (
    <div className="calendar-card">
      <div className="calendar-header">
        <CalendarIcon size={16} />
        <span>Calendar</span>
        {loading && <Loader2 size={16} className="animate-spin" />}
      </div>
      
      {error && (
        <div className="calendar-error">
          <span className="error-text">{error}</span>
        </div>
      )}
      
      <div className="month-controls">
        <h3>{currentMonthDisplay}</h3>
        <div className="nav-buttons">
          <button onClick={goToPreviousMonth}><ChevronLeft size={16} /></button>
          <button onClick={goToNextMonth}><ChevronRight size={16} /></button>
        </div>
      </div>
      
      <div className="calendar-grid">
        {calendarColumns.map((column, i) => (
          <div key={i} className="day-column">
            <div className="day-label">{days[i]}</div>
            {column.map((dateObj, j) => {
              const eventType = getEventType(dateObj);
              const dateKey = formatDateKey(dateObj.fullDate);
              const isSaving = savingEvent === dateKey;
              
              return (
                <div 
                  key={j} 
                  className={`date-cell ${eventType} ${dateObj.month !== 'current' ? 'dimmed' : ''} ${isSaving ? 'saving' : ''}`}
                  onClick={() => !isSaving && handleDateClick(dateObj)}
                  title={isSaving ? 'Saving...' : (eventType ? `${eventType.charAt(0).toUpperCase() + eventType.slice(1)}` : 'Click to add event')}
                >
                  <span className="date-number">{dateObj.date}</span>
                  {isSaving && <Loader2 size={10} className="date-spinner animate-spin" />}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="calendar-legend">
        <div className="legend-item planting">Planting</div>
        <div className="legend-item harvest">Harvest</div>
        <div className="legend-item meeting">Meeting</div>
        <div className="legend-item inspection">Inspection</div>
        <div className="legend-item delivery">Delivery</div>
      </div>
      <div style={{ marginTop: "10px" }}><small>Click on a date to add event type</small></div>
    </div>
  );
}